import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import PersonCell from '../../../components/ui/PersonCell';

export const APPROVAL_PERIODS = ['day_1_30', 'day_31_60', 'day_61_90'];
export const APPROVAL_STATUSES = ['requested', 'approved', 'rejected'];

const multiSelectFilter = (label, options, valueRender) => ({
  filter: true,
  sort: true,
  filterType: 'custom',
  filterOptions: {
    defaultOp: 'in',
    display: (filterList, onChange, index, column) => {
      const current = filterList[index] && filterList[index][0]
        ? filterList[index][0].split(',').filter(Boolean)
        : [];
      return (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            value={current}
            onChange={(e) => {
              const v = e.target.value;
              const csv = Array.isArray(v) ? v.filter(Boolean).join(',') : '';
              filterList[index] = csv ? [csv] : [];
              onChange(filterList[index], index, column);
            }}
            renderValue={(selected) => selected.map((s) => valueRender ? valueRender(s) : s).join(', ')}
          >
            {options.map((o) => (
              <MenuItem key={o} value={o}>{valueRender ? valueRender(o) : o}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
  },
});

const useObPeriodApprovalsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/onboarding-period-approvals?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const approve = useCallback(async (id, note) => {
    try { await api.post(`/v1/onboarding-period-approvals/${id}/approve`, { note }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  // OBB-01 — İK vekâleti. Normal `approve` artık YALNIZ beklenen yöneticinin
  // çağırabildiği bir uçtur; İK bu ayrı uçtan, gerekçe vererek onaylar ve kayıt
  // "İK adına" işaretlenir (kronolojide yöneticinin adı BASILMAZ).
  const approveOnBehalf = useCallback(async (id, reason) => {
    try { await api.post(`/v1/onboarding-period-approvals/${id}/approve-on-behalf`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const reject = useCallback(async (id, reason) => {
    try { await api.post(`/v1/onboarding-period-approvals/${id}/reject`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  // Safety valve: waive the employee's acknowledgment (unresponsive new hire) so
  // a manager-approved checkpoint can advance.
  const overrideEmployeeAck = useCallback(async (id, reason) => {
    try { await api.post(`/v1/onboarding-period-approvals/${id}/override-employee-ack`, { reason }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => {
    const periodLabel = (s) => t(`onboardingPeriodApproval.period.${s}`, { defaultValue: s });
    const statusLabel = (s) => t(`onboardingPeriodApproval.status.${s}`, { defaultValue: s });

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      {
        name: 'personFullName',
        label: t('onboardingPeriodApproval.person'),
        options: {
          customBodyRender: (v, tableMeta) => (
            <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.personProfilePicture} />
          ), filter: false, sort: true },
      },
      {
        name: 'personEmail',
        label: t('onboardingPeriodApproval.email'),
        options: { filter: false, sort: false },
      },
      {
        name: 'period',
        label: t('onboardingPeriodApproval.periodLabel'),
        options: {
          ...multiSelectFilter(t('onboardingPeriodApproval.periodLabel'), APPROVAL_PERIODS, periodLabel),
          customBodyRender: (v) => periodLabel(v),
        },
      },
      {
        name: 'status',
        label: t('onboardingPeriodApproval.statusLabel'),
        options: {
          ...multiSelectFilter(t('onboardingPeriodApproval.statusLabel'), APPROVAL_STATUSES, statusLabel),
          // A manager-approved row still waits on the employee's co-sign — make that
          // legible instead of a bare "Onaylandı" that reads as done (complaint B).
          customBodyRender: (v, tableMeta) => {
            const base = statusLabel(v);
            const row = data[tableMeta.rowIndex];
            if (row && v === 'approved' && !row.employeeAcknowledgedAt) {
              return (
                <div>
                  <div>{base}</div>
                  <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
                    {t('onboardingPeriodApproval.awaitingEmployeeAck')}
                  </div>
                </div>
              );
            }
            return base;
          },
        },
      },
      dateFilterColumn('requestedAt', t('onboardingPeriodApproval.requestedAt'), lang, { includeTime: true }),
      {
        name: 'approvedAt',
        label: t('onboardingPeriodApproval.approvedAt'),
        options: { filter: false, sort: true, customBodyRender: (v) => v ? new Date(v).toLocaleString() : '-' },
      },
    ];
  }, [t, lang, data]);

  return { fetching, data, pagination, columns, refresh, approve, approveOnBehalf, reject, overrideEmployeeAck };
};

export default useObPeriodApprovalsApi;
